import { useFormikContext } from 'formik';
import { ReactElement, useEffect, useState } from 'react';
import { MAX_DATE } from 'src/types/Constants';
import { FormDropdownChangeEventConfig } from 'src/types/propTypes/FormDropdownPropTypes';
import CustomFormTextbox from '../../../components/FormInputs/CustomFormTextbox/CustomFormTextbox';
import FormCheckboxList from '../../../components/FormInputs/FormCheckboxList/FormCheckboxList';
import FormDateSelector from '../../../components/FormInputs/FormDateSelector/FormDateSelector';
import FormDropdown from '../../../components/FormInputs/FormDropdown/FormDropdown';
import FormRadioList from '../../../components/FormInputs/FormRadioList/FormRadioList';
import FormTextbox from '../../../components/FormInputs/FormTextbox/FormTextbox';
import Panel from '../../../components/Panel/Panel';
import { useAppSelector } from '../../../redux/hooks';
import {
  selectSchoolManagementDataEntryMode,
  selectSchoolManagementFormViewModel,
} from '../../../redux/slices/schoolManagementSlice';
import { getCurrentUserAccess } from '../../../services/base.service';
import { DataEntryMode } from '../../../types/DataEntryMode';
import { PanelType } from '../../../types/PanelType';
import { FormRadioListOrientation } from '../../../types/propTypes/FormRadioListPropTypes';
import { FormTextboxChangeEventConfig } from '../../../types/propTypes/FormTextboxPropTypes';
import { SchoolManagementFormViewModel } from '../../../types/SchoolManagementFormViewModel';
import { SchoolManagementModel } from '../../../types/SchoolManagementModel';
import {
  getSchoolManagementValidationSchema,
  SchoolManagementValidationEditSchema,
} from '../../../types/Validation/SchoolManagementValidationSchema';
import {
  isDistrictAdmin,
  isStateAdmin,
} from '../../../utilities/userUtilities';

const SchoolManagementSchoolInformationPanel = (): ReactElement => {
  const { setFieldValue, values } = useFormikContext<SchoolManagementModel>();

  const formViewModel: SchoolManagementFormViewModel = useAppSelector(
    selectSchoolManagementFormViewModel
  );

  const [isReportedFederallyDisabled, setIsReportedFederallyDisabled] =
    useState(false);

  const dataEntryMode: DataEntryMode = useAppSelector(
    selectSchoolManagementDataEntryMode
  );

  const isScheduleTypeRequired =
    values.schoolType === 'Regular School' &&
    !['001', '200', '500'].includes(values.schoolIdentifierSchoolPart) &&
    (dataEntryMode === DataEntryMode.EDIT ||
      (dataEntryMode === DataEntryMode.NEW && !isStateAdmin()));
  const validationSchema =
    dataEntryMode === DataEntryMode.NEW
      ? getSchoolManagementValidationSchema(
          formViewModel.existingSchoolIdentifiers
        )
      : SchoolManagementValidationEditSchema;

  const updateReportedFederally = async (
    config: FormTextboxChangeEventConfig
  ): Promise<void> => {
    let reportedFederally =
      config.value === '500' || config.value === '200' ? 'No' : 'Yes';
    let reportedFederallyDisabled = reportedFederally === 'No' ? true : false;

    /* istanbul ignore next */
    if (config.setValueHook && reportedFederally) {
      if (dataEntryMode === DataEntryMode.EDIT) {
        reportedFederallyDisabled = false;
      }
      setIsReportedFederallyDisabled(reportedFederallyDisabled);

      if (reportedFederally === 'Yes') {
        reportedFederally = '';
      }
      config.setValueHook('reportedFederally', reportedFederally);
    }
  };

  useEffect(() => {
    if (values.charterSchool === '' && values.charterSchoolType === '') {
      const user = getCurrentUserAccess();
      const districtType = user?.currentDistrict?.type || '';
      const isCharter = districtType === 'Independent Charter District';

      Promise.all([
        setFieldValue('charterSchool', isCharter ? 'Yes' : 'No', false),
        setFieldValue(
          'charterSchoolType',
          isCharter ? '' : 'Not a Charter School',
          false
        ),
      ]);
    }
  }, [setFieldValue, values.charterSchool, values.charterSchoolType]);

  useEffect(() => {
    if (values.charterSchool === 'No') {
      setFieldValue('charterSchoolType', 'Not a Charter School', false);
    } else if (values.charterSchool === 'Yes') {
      if (values.charterSchoolType === 'Not a Charter School') {
        setFieldValue('charterSchoolType', '', false);
      }
    }
  }, [values.charterSchool, setFieldValue, values.charterSchoolType]);

  useEffect(() => {
    if (values.schoolType !== 'Regular School' && values.scheduleType) {
      setFieldValue('scheduleType', '', false);
    }
  }, [values.schoolType, setFieldValue, values.scheduleType]);

  const handleCharterSchoolTypeChange = async (
    config: FormDropdownChangeEventConfig
  ): Promise<void> => {
    setFieldValue(config.field, config.value);
  };

  return (
    <Panel panelType={PanelType.INFO} heading="School Information">
      <div className="field-row">
        <FormTextbox
          displayName="Name of Institution"
          field="schoolName"
          validationSchema={validationSchema}
          disabled={!isStateAdmin()}
        />
        <CustomFormTextbox
          displayName="School Identifier"
          field1="schoolIdentifierLeaPart"
          field2="schoolIdentifierSchoolPart"
          validationSchema={validationSchema}
          onChangeEvent={updateReportedFederally}
          disabled={!isStateAdmin()}
        />
        <FormTextbox
          displayName="LEA Identifier"
          field="leaIdentifier"
          disabled={true}
        />
      </div>

      <div className="field-row field-row-grid-2-1-1-1">
        <FormDropdown
          displayName="School Type"
          field="schoolType"
          options={formViewModel.schoolTypeOptions}
          validationSchema={validationSchema}
          disabled={!isStateAdmin()}
          isClearable
        />
        <FormRadioList
          displayName="Reported Federally"
          field="reportedFederally"
          options={[
            { text: 'Yes', value: 'Yes' },
            { text: 'No', value: 'No' },
          ]}
          optionOrientation={FormRadioListOrientation.Horizontal}
          validationSchema={validationSchema}
          disabled={isReportedFederallyDisabled || !isStateAdmin()}
        />

        <FormTextbox
          displayName="ACT Identifier"
          field="actIdentifier"
          validationSchema={validationSchema}
          disabled={!isStateAdmin()}
        />
        <FormTextbox
          displayName="NCES Identifier"
          field="ncesIdentifier"
          validationSchema={validationSchema}
          disabled={!isStateAdmin()}
        />
      </div>
      <div className="field-row">
        <FormRadioList
          displayName="Charter School"
          field="charterSchool"
          options={[
            { text: 'Yes', value: 'Yes' },
            { text: 'No', value: 'No' },
          ]}
          optionOrientation={FormRadioListOrientation.Horizontal}
          validationSchema={validationSchema}
          disabled={true}
        />
        <FormDropdown
          displayName="Charter School Type"
          field="charterSchoolType"
          options={formViewModel.charterSchoolTypeOptions.map((option) => ({
            ...option,
            isDisabled: false,
          }))}
          validationSchema={validationSchema}
          onChangeEvent={handleCharterSchoolTypeChange}
          disabled={!isStateAdmin()}
          isClearable
        />
        <FormDropdown
          displayName="School Operational Status"
          field="schoolOperationalStatus"
          options={formViewModel.schoolOperationalStatusOptions}
          validationSchema={validationSchema}
          disabled={!isStateAdmin()}
          isClearable
        />

        <FormDateSelector
          displayName="Operational Status Effective Date"
          field="operationalStatusEffectiveDate"
          validationSchema={validationSchema}
          disabled={!isStateAdmin()}
          maxDate={MAX_DATE}
          isClearable
        />
      </div>

      <div className="field-row">
        <FormTextbox
          displayName="Website"
          field="website"
          validationSchema={validationSchema}
          disabled={!isStateAdmin()}
        />
        <FormTextbox
          displayName="Telephone Number"
          field="phone"
          validationSchema={validationSchema}
          disabled={!isStateAdmin()}
        />
      </div>
      <div className="field-row">
        <FormCheckboxList
          displayName="Grades"
          field="grades"
          options={formViewModel.gradeOptions}
          addAllCheckbox={true}
          classConfig={{ containerClasses: 'grades-container' }}
          validationSchema={validationSchema}
          disabled={!isStateAdmin()}
        />
        <FormRadioList
          displayName="Magnet School"
          field="magnetSchool"
          options={[
            { text: 'Yes', value: 'Yes' },
            { text: 'No', value: 'No' },
          ]}
          optionOrientation={FormRadioListOrientation.Horizontal}
          validationSchema={validationSchema}
          disabled={!isStateAdmin()}
        />
        <FormRadioList
          displayName="Virtual School"
          field="virtualSchool"
          options={[
            { text: 'Yes', value: 'Yes' },
            { text: 'No', value: 'No' },
          ]}
          optionOrientation={FormRadioListOrientation.Horizontal}
          validationSchema={validationSchema}
          disabled={!isStateAdmin()}
        />
      </div>
      {values.schoolType === 'Regular School' && (
        <div className="field-row field-row-grid-1-1-1">
          <FormDropdown
            displayName="Schedule Type"
            field="scheduleType"
            options={formViewModel.scheduleTypeOptions}
            validationSchema={validationSchema}
            disabled={!isStateAdmin() && !isDistrictAdmin()}
            classConfig={{
              labelClasses: isScheduleTypeRequired
                ? 'forced-required-label'
                : '',
            }}
          />
        </div>
      )}
    </Panel>
  );
};

export default SchoolManagementSchoolInformationPanel;
